/**
 * Dropdown.jsx
 * Created by michael bray on 7/14/17.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import { find } from 'lodash';
import cx from 'classnames';

import DropdownOption from './DropdownOption';
import Label from '../Label';
import * as Icons from '../icons/Icons';

const propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.array,
    changeSelection: PropTypes.func,
    currentSelection: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
    showCloseButton: PropTypes.bool,
    className: PropTypes.string,
    isRequired: PropTypes.bool,
    isLabelVisible: PropTypes.bool,
    placeholderText: PropTypes.string,
    hasError: PropTypes.bool,
    /** The ref to be passed to the input. Must use React.createRef() */
    innerRef: PropTypes.shape({ current: PropTypes.node })
};

const defaultProps = {
    options: [],
    disabled: false,
    isRequired: false,
    showCloseButton: false,
    isLabelVisible: true,
    hasError: false,
    placeholderText: 'Select an option...'
};

export default class Dropdown extends React.PureComponent {
    constructor(props) {
        super(props);

        let focusIndex = 0;

        if (props.currentSelection) {
            focusIndex = props.options.findIndex((item) => {
                return item.value === props.currentSelection
            })
        }

        this.state = {
            showDropdown: false,
            focusIndex
        };

        this.recSelect = props.innerRef || React.createRef();
    }

    componentDidMount() {
        window.addEventListener('click', this.toggleDropdown);
        window.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        window.removeEventListener('click', this.toggleDropdown);
        window.removeEventListener('keydown', this.handleKeyDown);
    }

    // We need to use findDOMNode for this functionality to work
    /* eslint-disable react/no-find-dom-node */
    toggleDropdown = (event) => {
        if (this.props.disabled) {
            return;
        }

        const { currentSelection, options } = this.props;

        // Because we're not overriding the tab key, we need to update
        // the focusIndex each time the input is opened, otherwise
        // the focusIndex could get out of sync with the actual selection
        // and the wrong item would have focus on a re-open
        let focusIndex = 0;

        if (currentSelection) {
            focusIndex = options.findIndex((item) => {
                return item.value === currentSelection
            })
        }

        if (findDOMNode(this).contains(event.target)) {
            event.preventDefault();

            this.setState({
                showDropdown: !this.state.showDropdown,
                focusIndex
            });
        }
        else if (!findDOMNode(this).contains(event.target) && this.state.showDropdown) {
            this.setState({
                showDropdown: false,
                focusIndex
            });
        }
    }
    /* eslint-enable react/no-find-dom-node */

    clickedItem = (value) => {
        this.props.changeSelection(value);

        this.setState({
            showDropdown: false
        });

        setTimeout(() => {
            this.recSelect.current.focus();
        }, 0);
    }

    closePopup = (event, resetFocus = true) => {
        if (event) {
            event.stopPropagation();
        }

        this.setState({
            showDropdown: false
        });

        if (resetFocus) {
            this.recSelect.current.focus();
        }
    }

    removeCurrentSelection = () => {
        this.props.changeSelection('');
    }

    handleKeyDown = (event) => {
        const { showDropdown } = this.state;
        const { id } = this.props;
        const key = event.key || event.keyCode;

        if (!showDropdown) {
            return;
        }

        switch (key) {
            case 'Escape' || 'Esc' || 27:
                event.preventDefault();
                this.closePopup();
                break;
            case 'ArrowDown' || 40:
                event.preventDefault();
                this.selectNextOption();
                break;
            case 'ArrowUp' || 38:
                event.preventDefault();
                this.selectPreviousOption();
                break;
            case 'Home' || 36:
                event.preventDefault();
                this.selectFirstOption();
                break;
            case 'End' || 35:
                event.preventDefault();
                this.selectLastOption();
                break;
            case 'Tab' || 9:
                // If user shift-tabs from the main select, then close popup
                if (event.shiftKey && document.activeElement.id === `${id}-button`) {
                    this.closePopup(null, false);
                    break;
                }
                // if user tabs from the close button, then close popup
                if (!event.shiftKey && document.activeElement.id === `${id}-close-button`) {
                    this.closePopup(null, false);
                }
                break;
            default:
                break;
        }
    }

    selectNextOption = () => {
        const { focusIndex } = this.state;
        const { options } = this.props;

        if (focusIndex < options.length - 1) {
            // Disabled options could be anywhere and in any number! Instead of
            // just incrementing focusIndex, we search for the next option that
            // isn't disabled. This method is repeated for selectPreviousOption,
            // selectFirstOption, and selectLastOption
            let nextAvailableIndex = focusIndex;

            for (let i = focusIndex; i < options.length - 1; i += 1) {
                if (!options[i + 1].disabled) {
                    nextAvailableIndex = i + 1;
                    break;
                }
            }
            this.setState({
                focusIndex: nextAvailableIndex
            });
        }
    }

    selectPreviousOption = () => {
        const { focusIndex } = this.state;
        const { options } = this.props;

        if (focusIndex > 0) {
            let previousAvailableIndex = focusIndex;

            for (let i = focusIndex; i > 0; i -= 1) {
                if (!options[i - 1].disabled) {
                    previousAvailableIndex = i - 1;
                    break;
                }
            }
            this.setState({
                focusIndex: previousAvailableIndex
            });
        }
    }

    selectFirstOption = () => {
        const { focusIndex } = this.state;
        const { options } = this.props;

        let firstOption = focusIndex;

        for (let i = 0; i < options.length - 1; i += 1) {
            if (!options[i].disabled) {
                firstOption = i;
                break;
            }
        }
        this.setState({
            focusIndex: firstOption
        });
    }

    selectLastOption = () => {
        const { focusIndex } = this.state;
        const { options } = this.props;

        let lastOption = focusIndex;

        for (let i = options.length - 1; i > 0; i -= 1) {
            if (!options[i].disabled) {
                lastOption = i;
                break;
            }
        }
        this.setState({
            focusIndex: lastOption
        });
    }

    render() {
        const {
            label,
            id,
            disabled,
            isRequired,
            currentSelection,
            className,
            options,
            showCloseButton,
            isLabelVisible,
            placeholderText,
            hasError
        } = this.props;

        if (process.env.NODE_ENV === 'development') {
            console.warn('~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~');
            console.warn('Dropdown is not 508 compliant and has been deprecated. Use Select instead.');
            console.warn('~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~');
        }

        const { showDropdown, focusIndex } = this.state;

        // Set border and icon based on state's open/closed value
        const icon = showDropdown
            ? <Icons.IconArrowUp alt="" />
            : <Icons.IconArrowDown alt="" />;

        // Set the currently shown data
        let currentField = find(options, { value: currentSelection });
        if (!currentField) {
            currentField = {
                label: '',
                value: ''
            };
        }

        let selectIcon = null;
        let removeSelection = null;

        if (currentSelection && !disabled && showCloseButton) {
            removeSelection = (
                <span className="rec-select-label">
                    <button
                        className="rec-remove-selection"
                        onClick={this.removeCurrentSelection}>
                        <Icons.IconCloseCircle alt="Remove selection" />
                    </button>
                </span>
            );
        }
        else {
            selectIcon = (
                <span className="rec-select-icon">
                    {icon}
                </span>
            );
        }

        return (
            <div id={id} className="rec-dropdown">
                <Label
                    htmlFor={`${id}-select`}
                    isRequired={isRequired}
                    isVisible={isLabelVisible}>
                    {label}
                </Label>
                <div className="rec-select-holder">
                    <button
                        ref={this.recSelect}
                        id={`${id}-select`}
                        className={cx('rec-select', className, {
                            'open': showDropdown,
                            'disabled': disabled,
                            'rec-select-has-error': hasError
                        })}
                        disabled={disabled}
                        aria-labelledby={`${id}-select-label`}
                        aria-haspopup="listbox"
                        type="button">
                        {!currentField.label || currentField.value === ''
                            ? <span className="rec-select-label rec-select-label-placeholder">{placeholderText}</span>
                            : <span className="rec-select-label">{currentField.label}</span>
                        }
                        {selectIcon}
                    </button>
                    {removeSelection}
                    {showDropdown && (
                        <div className="rec-select-options">
                            <ul
                                role="listbox"
                                aria-labelledby={`${id}-select-label`}>
                                {options.map((field, index) => (
                                    <DropdownOption
                                        key={field.value}
                                        className={field.className}
                                        value={field.value}
                                        onClick={this.clickedItem}
                                        isDisabled={field.disabled}
                                        isSelected={field.value === currentSelection}
                                        isFocused={focusIndex === index}>
                                        {field.label}
                                    </DropdownOption>))}
                            </ul>
                            <div className="rec-select-option-last">
                                <button
                                    ref={(node) => this.closeButton = node}
                                    id={`${id}-close-button`}
                                    className="rec-close-dropdown-button"
                                    onClick={this.closePopup}
                                    aria-label="Close"
                                    type="button">
                                    Close
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

Dropdown.propTypes = propTypes;
Dropdown.defaultProps = defaultProps;
